import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Search,
  Settings,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  entryLookup,
  listInboundCalls,
  listKnowledgeEntries,
} from "./inboundCallsApi";
import {
  addDays,
  applyFilters,
  breakdownByOutcome,
  breakdownByReason,
  buildDailyVolume,
  CallFilters,
  computeKpis,
  DAY_TYPE_LABELS,
  DaySection,
  DayType,
  formatCallTime,
  formatWeekLabel,
  getDayKey,
  groupByCaller,
  groupByDay,
  isUrgent,
  KNOWLEDGE_SOURCE_LABELS,
  KnowledgeSource,
  listCallerOptions,
  maskPhone,
  OUTCOME_LABELS,
  REASON_LABELS,
  SORT_MODE_LABELS,
  sortCalls,
  SortMode,
  TIME_OF_DAY_LABELS,
  TimeOfDay,
  weekStartKey,
} from "./selectors";
import {
  CallOutcome,
  InboundCall,
  KnowledgeEntry,
  ReasonCategory,
} from "./types";
import OutcomeBadge, { hasOutcome, UrgentBadge } from "./OutcomeBadge";
import {
  APPOINTMENT_CATEGORIES_PATH,
  KNOWLEDGE_BANK_PATH,
  PROVIDER_SCHEDULING_PATH,
} from "./paths";

const SETTINGS_MENU_ITEMS = [
  { to: KNOWLEDGE_BANK_PATH, label: "Knowledge bank", testId: "inbound-settings-knowledge_bank" },
  { to: APPOINTMENT_CATEGORIES_PATH, label: "Categories", testId: "inbound-settings-appointment_categories" },
  { to: PROVIDER_SCHEDULING_PATH, label: "Scheduling", testId: "inbound-settings-provider_scheduling" },
] as const;

const ALL = "__all__";

/**
 * Toggle buttons keep their border in both states so the row of controls stays
 * visually stable; only the fill and text colour change to signal "on".
 */
const toggleColors = (active: boolean) =>
  active
    ? "bg-brand text-white border-brand hover:bg-brand-hover hover:text-white"
    : "bg-background text-gray-700 border-input hover:bg-gray-100 hover:text-gray-900";

interface KpiCardProps {
  label: string;
  value: number;
  tooltip: string;
}

function KpiCard({ label, value, tooltip }: KpiCardProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="cursor-help">
          <Card className="gap-1 py-4">
            <CardContent className="px-4">
              <div
                className="text-2xl font-semibold text-gray-900"
                data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {value}
              </div>
              <div className="text-xs text-gray-500">{label}</div>
            </CardContent>
          </Card>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 text-xs" side="top">
        {tooltip}
      </PopoverContent>
    </Popover>
  );
}

function BreakdownBars({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: string; count: number }>;
}) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <Card className="gap-2 py-4">
      <CardContent className="px-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.key} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-44 truncate shrink-0">
                {row.label}
              </span>
              <div className="flex-1 bg-gray-100 rounded h-3">
                <div
                  className="bg-brand rounded h-3"
                  style={{ width: `${(row.count / max) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-700 w-6 text-right">
                {row.count}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function InboundCallsPage() {
  const navigate = useNavigate();

  // undefined = still loading; [] after a failed load so the page settles on
  // its empty state instead of a permanent spinner.
  const [calls, setCalls] = useState<InboundCall[] | undefined>(undefined);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    listInboundCalls()
      .then((loadedCalls) => {
        if (!cancelled) {
          setCalls(loadedCalls);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Could not load calls.");
          setCalls([]);
        }
      });
    // The bank only feeds search and the knowledge filter; the list renders without it.
    listKnowledgeEntries()
      .then((loadedEntries) => {
        if (!cancelled) {
          setEntries(loadedEntries);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const allCalls = useMemo(() => calls ?? [], [calls]);
  const lookup = useMemo(() => entryLookup(entries), [entries]);
  // One pilot line, one zone. A team with lines in two zones would need per-call formatting.
  const timezone =
    allCalls[0]?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const practices = useMemo(() => {
    const byId = new Map<string, string>();
    for (const call of allCalls) {
      if (!byId.has(call.practiceId)) {
        byId.set(call.practiceId, call.practiceName ?? call.practiceId);
      }
    }
    return Array.from(byId, ([id, name]) => ({ id, name }));
  }, [allCalls]);
  const practiceName = (practiceId: string) =>
    practices.find((practice) => practice.id === practiceId)?.name ?? practiceId;

  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dayTypeFilter, setDayTypeFilter] = useState<string>(ALL);
  const [timeOfDayFilter, setTimeOfDayFilter] = useState<string>(ALL);
  const [practiceFilter, setPracticeFilter] = useState<string>(ALL);
  const [callerFilter, setCallerFilter] = useState<string>(ALL);
  const [repeatCallersOnly, setRepeatCallersOnly] = useState(false);
  const [reasonFilter, setReasonFilter] = useState<string>(ALL);
  const [outcomeFilter, setOutcomeFilter] = useState<string>(ALL);
  const [knowledgeFilter, setKnowledgeFilter] = useState<string>(ALL);
  const [sortMode, setSortMode] = useState<SortMode>("NEWEST");
  const [groupMode, setGroupMode] = useState<"day" | "caller">("day");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const filters: CallFilters = useMemo(
    () => ({
      query: query.trim() === "" ? undefined : query,
      dateFrom: dateFrom === "" ? undefined : dateFrom,
      dateTo: dateTo === "" ? undefined : dateTo,
      dayType: dayTypeFilter === ALL ? undefined : (dayTypeFilter as DayType),
      timeOfDay:
        timeOfDayFilter === ALL ? undefined : (timeOfDayFilter as TimeOfDay),
      practiceId: practiceFilter === ALL ? undefined : practiceFilter,
      callerPhone: callerFilter === ALL ? undefined : callerFilter,
      repeatCallersOnly: repeatCallersOnly || undefined,
      reason: reasonFilter === ALL ? undefined : (reasonFilter as ReasonCategory),
      outcome: outcomeFilter === ALL ? undefined : (outcomeFilter as CallOutcome),
      knowledgeSource:
        knowledgeFilter === ALL ? undefined : (knowledgeFilter as KnowledgeSource),
    }),
    [
      query,
      dateFrom,
      dateTo,
      dayTypeFilter,
      timeOfDayFilter,
      practiceFilter,
      callerFilter,
      repeatCallersOnly,
      reasonFilter,
      outcomeFilter,
      knowledgeFilter,
    ]
  );

  const filteredCalls = useMemo(
    () =>
      sortCalls(applyFilters(allCalls, filters, timezone, lookup), sortMode),
    [allCalls, filters, timezone, lookup, sortMode]
  );

  // The chart is a date control, so it ignores its own dimension: bars keep
  // showing the other days you could switch to.
  const chartCalls = useMemo(
    () =>
      applyFilters(
        allCalls,
        { ...filters, dateFrom: undefined, dateTo: undefined },
        timezone,
        lookup
      ),
    [allCalls, filters, timezone, lookup]
  );

  // Outcome metrics only count finished calls; in-flight and failed rows are
  // listed but have no outcome yet. Every row is still a call the practice got.
  const finishedCalls = useMemo(
    () => filteredCalls.filter(hasOutcome),
    [filteredCalls]
  );
  const kpis = useMemo(
    () => ({ ...computeKpis(finishedCalls), totalCalls: filteredCalls.length }),
    [finishedCalls, filteredCalls]
  );
  // The chart shows one week at a time. It opens on the week of the newest
  // call, and the stepper is bounded by the oldest call and the current week.
  const [weekStartOverride, setWeekStartOverride] = useState<string>();
  const callDayKeys = useMemo(
    () => allCalls.map((call) => getDayKey(call.receivedAt, timezone)).sort(),
    [allCalls, timezone]
  );
  const currentWeekStart = weekStartKey(
    getDayKey(new Date().toISOString(), timezone)
  );
  const latestWeekStart =
    callDayKeys.length > 0
      ? weekStartKey(callDayKeys[callDayKeys.length - 1])
      : currentWeekStart;
  const earliestWeekStart =
    callDayKeys.length > 0 ? weekStartKey(callDayKeys[0]) : currentWeekStart;
  const lastWeekStart =
    latestWeekStart > currentWeekStart ? latestWeekStart : currentWeekStart;
  const weekStart = weekStartOverride ?? latestWeekStart;
  const dailyVolume = useMemo(
    () => buildDailyVolume(chartCalls, timezone, weekStart),
    [chartCalls, timezone, weekStart]
  );
  const weekTotal = dailyVolume.reduce((sum, bucket) => sum + bucket.count, 0);
  const reasonRows = useMemo(
    () => breakdownByReason(finishedCalls),
    [finishedCalls]
  );
  const outcomeRows = useMemo(
    () => breakdownByOutcome(finishedCalls),
    [finishedCalls]
  );

  const callerOptions = useMemo(() => listCallerOptions(allCalls), [allCalls]);
  const callerGroups = useMemo(
    () => groupByCaller(filteredCalls),
    [filteredCalls]
  );
  const daySections = useMemo(
    () => groupByDay(filteredCalls, timezone),
    [filteredCalls, timezone]
  );

  // Day headers only make sense while the list runs in date order; sorting by
  // caller interleaves days and would put a header above nearly every row.
  const showDaySeparators = sortMode !== "CALLER";

  const maxDailyCount = Math.max(...dailyVolume.map((b) => b.count), 1);

  /** A bar is "selected" when the range has been narrowed to exactly that day. */
  const selectedDayKey =
    dateFrom !== "" && dateFrom === dateTo ? dateFrom : undefined;

  const toggleDay = (dayKey: string) => {
    if (selectedDayKey === dayKey) {
      setDateFrom("");
      setDateTo("");
    } else {
      setDateFrom(dayKey);
      setDateTo(dayKey);
    }
  };

  const resetFilters = () => {
    setQuery("");
    setDateFrom("");
    setDateTo("");
    setDayTypeFilter(ALL);
    setTimeOfDayFilter(ALL);
    setPracticeFilter(ALL);
    setCallerFilter(ALL);
    setRepeatCallersOnly(false);
    setReasonFilter(ALL);
    setOutcomeFilter(ALL);
    setKnowledgeFilter(ALL);
  };

  const callerLabel = (normalizedPhone: string) =>
    callerOptions.find((c) => c.normalizedPhone === normalizedPhone)
      ?.callerName ?? normalizedPhone;

  // Chips make a stacked combination of filters legible and individually
  // removable, instead of hiding state inside a dozen collapsed controls.
  const activeChips: Array<{ key: string; label: string; clear: () => void }> =
    [];
  if (query.trim() !== "") {
    activeChips.push({
      key: "query",
      label: `Search: “${query.trim()}”`,
      clear: () => setQuery(""),
    });
  }
  if (selectedDayKey !== undefined) {
    activeChips.push({
      key: "day",
      label: `Day: ${selectedDayKey}`,
      clear: () => {
        setDateFrom("");
        setDateTo("");
      },
    });
  } else if (dateFrom !== "" || dateTo !== "") {
    activeChips.push({
      key: "range",
      label: `Dates: ${dateFrom || "any"} → ${dateTo || "any"}`,
      clear: () => {
        setDateFrom("");
        setDateTo("");
      },
    });
  }
  if (dayTypeFilter !== ALL) {
    activeChips.push({
      key: "dayType",
      label: DAY_TYPE_LABELS[dayTypeFilter as DayType],
      clear: () => setDayTypeFilter(ALL),
    });
  }
  if (timeOfDayFilter !== ALL) {
    activeChips.push({
      key: "timeOfDay",
      label: TIME_OF_DAY_LABELS[timeOfDayFilter as TimeOfDay],
      clear: () => setTimeOfDayFilter(ALL),
    });
  }
  if (practiceFilter !== ALL) {
    activeChips.push({
      key: "practice",
      label: practiceName(practiceFilter),
      clear: () => setPracticeFilter(ALL),
    });
  }
  if (callerFilter !== ALL) {
    activeChips.push({
      key: "caller",
      label: `Caller: ${callerLabel(callerFilter)}`,
      clear: () => setCallerFilter(ALL),
    });
  }
  if (repeatCallersOnly) {
    activeChips.push({
      key: "repeat",
      label: "Repeat callers only",
      clear: () => setRepeatCallersOnly(false),
    });
  }
  if (reasonFilter !== ALL) {
    activeChips.push({
      key: "reason",
      label: REASON_LABELS[reasonFilter as ReasonCategory],
      clear: () => setReasonFilter(ALL),
    });
  }
  if (outcomeFilter !== ALL) {
    activeChips.push({
      key: "outcome",
      label: OUTCOME_LABELS[outcomeFilter as CallOutcome],
      clear: () => setOutcomeFilter(ALL),
    });
  }
  if (knowledgeFilter !== ALL) {
    activeChips.push({
      key: "knowledge",
      label: KNOWLEDGE_SOURCE_LABELS[knowledgeFilter as KnowledgeSource],
      clear: () => setKnowledgeFilter(ALL),
    });
  }
  const advancedCount = [
    dateFrom !== "" || dateTo !== "",
    dayTypeFilter !== ALL,
    timeOfDayFilter !== ALL,
    callerFilter !== ALL,
    repeatCallersOnly,
    knowledgeFilter !== ALL,
  ].filter(Boolean).length;

  const openCall = (call: InboundCall) => {
    navigate(`/dashboard/inbound_calls/calls/${call.id}`);
  };

  const renderCallRow = (call: InboundCall) => (
    <TableRow
      key={call.id}
      data-testid={`inbound-call-row-${call.id}`}
      className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
      onClick={() => openCall(call)}
    >
      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
        {formatCallTime(call.receivedAt, timezone)}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          {call.callerName}
          {isUrgent(call) && <UrgentBadge />}
        </div>
        <div className="text-xs text-gray-500">{maskPhone(call.callerPhone)}</div>
      </TableCell>
      <TableCell className="hidden md:table-cell text-xs text-gray-700">
        {practiceName(call.practiceId)}
      </TableCell>
      <TableCell className="text-xs text-gray-700">
        {REASON_LABELS[call.reason]}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-xs text-gray-700 max-w-xs truncate">
        {call.recapText}
      </TableCell>
      <TableCell>
        <OutcomeBadge outcome={call.outcome} status={call.status} />
      </TableCell>
      <TableCell className="hidden xl:table-cell text-xs text-gray-700 max-w-[220px] truncate">
        {call.nextStep}
      </TableCell>
    </TableRow>
  );

  const renderCallCard = (call: InboundCall) => (
    <div
      key={call.id}
      className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-200"
      onClick={() => openCall(call)}
    >
      <div className="flex justify-between items-start gap-2">
        <span className="flex items-center gap-2 font-semibold text-gray-800 text-sm">
          {call.callerName}
          {isUrgent(call) && <UrgentBadge />}
        </span>
        <OutcomeBadge outcome={call.outcome} status={call.status} />
      </div>
      <p className="text-xs text-gray-600 mt-1">
        {REASON_LABELS[call.reason]} ·{" "}
        {formatCallTime(call.receivedAt, timezone)}
      </p>
      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
        {call.recapText}
      </p>
    </div>
  );

  /** Centered date bar marking where one day's calls end and the next begin. */
  const renderDaySeparatorRow = (section: DaySection) => (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={7} className="bg-gray-50 py-2">
        <div
          className="flex items-center gap-3"
          data-testid={`day-separator-${section.dayKey}`}
        >
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
            {section.label}
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
      </TableCell>
    </TableRow>
  );

  const tableHeader = (
    <TableHeader className="sticky top-0 bg-white z-0">
      <TableRow>
        <TableHead>Received</TableHead>
        <TableHead>Caller</TableHead>
        <TableHead className="hidden md:table-cell">Practice</TableHead>
        <TableHead>Reason</TableHead>
        <TableHead className="hidden lg:table-cell">Recap</TableHead>
        <TableHead>Outcome</TableHead>
        <TableHead className="hidden xl:table-cell">Next step</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="w-full bg-background">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold tracking-tight">
            Inbound Calls
          </h2>
          <div className="flex items-center gap-2">
            {calls === undefined && (
              <span className="text-xs text-muted-foreground">Loading calls…</span>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  aria-label="Inbound call settings"
                  data-testid="inbound-call-settings"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40 p-1">
                {SETTINGS_MENU_ITEMS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block rounded-sm px-2 py-1.5 text-xs text-gray-800 hover:bg-gray-100 truncate"
                    data-testid={item.testId}
                  >
                    {item.label}
                  </Link>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* KPI cards — these track the filters, so the numbers always match
            the rows in the table below. */}
        {activeChips.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Showing {filteredCalls.length} of {allCalls.length} calls. Metrics
            and breakdowns below reflect the active filters.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard
            label="Total calls"
            value={kpis.totalCalls}
            tooltip="Inbound calls matching the current filters, including abandoned calls."
          />
          <KpiCard
            label="Answered from knowledge bank"
            value={kpis.answeredFromKnowledge}
            tooltip="Calls fully resolved using an organization or practice knowledge_bank answer. No staff action needed."
          />
          <KpiCard
            label="Appointments scheduled"
            value={kpis.appointmentsScheduled}
            tooltip="Calls where the assistant booked an appointment directly — no staff action needed."
          />
          <KpiCard
            label="Appointment requests"
            value={kpis.appointmentRequests}
            tooltip="Calls about an appointment the assistant did not book — no fitting slot, a cancellation or reschedule, or the calendar was unavailable. Staff completes the request from the follow-up task."
          />
          <KpiCard
            label="Follow-ups needed"
            value={kpis.followUpsNeeded}
            tooltip="Calls with a pending staff follow-up task (callbacks, billing reviews, unanswered questions)."
          />
          <KpiCard
            label="Unresolved"
            value={kpis.unresolved}
            tooltip="Calls that ended without a resolution or captured next step (e.g. caller hung up before stating a need)."
          />
        </div>

        {/* Daily volume chart */}
        <Card className="gap-2 py-4">
          <CardContent className="px-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-sm font-medium text-gray-700">
                Call volume by day
              </h3>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Previous week"
                  disabled={weekStart <= earliestWeekStart}
                  onClick={() => setWeekStartOverride(addDays(weekStart, -7))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span
                  className="text-xs text-gray-700 min-w-[11rem] text-center"
                  data-testid="week-label"
                >
                  {formatWeekLabel(weekStart)} · {weekTotal}{" "}
                  {weekTotal === 1 ? "call" : "calls"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Next week"
                  disabled={weekStart >= lastWeekStart}
                  onClick={() => setWeekStartOverride(addDays(weekStart, 7))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {weekStart !== latestWeekStart && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setWeekStartOverride(undefined)}
                  >
                    Latest week
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm bg-brand" />
                  Weekday
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm bg-amber-400" />
                  Weekend
                </span>
              </div>
            </div>
            <div className="flex items-end gap-2 h-32">
              {dailyVolume.map((bucket) => {
                const isSelected = bucket.dayKey === selectedDayKey;
                return (
                  <button
                    key={bucket.dayKey}
                    data-testid={`day-bar-${bucket.dayKey}`}
                    aria-label={`${bucket.label}: ${bucket.count} calls`}
                    aria-pressed={isSelected}
                    className="flex-1 flex flex-col items-center gap-1 group"
                    onClick={() => toggleDay(bucket.dayKey)}
                  >
                    <span className="text-xs text-gray-600">
                      {bucket.count}
                    </span>
                    <div
                      className={`w-full rounded-t transition-all ${
                        bucket.isWeekend ? "bg-amber-400" : "bg-brand"
                      } ${
                        isSelected
                          ? "ring-2 ring-offset-1 ring-gray-800"
                          : "opacity-80 group-hover:opacity-100"
                      }`}
                      style={{
                        height: `${(bucket.count / maxDailyCount) * 80}px`,
                        minHeight: "6px",
                      }}
                    />
                    <span
                      className={`text-[10px] ${
                        isSelected
                          ? "font-semibold text-gray-900"
                          : "text-gray-500"
                      }`}
                    >
                      {bucket.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedDayKey && (
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => toggleDay(selectedDayKey)}
                >
                  <X className="h-3 w-3 mr-1" />
                  Showing {selectedDayKey} — clear day filter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breakdowns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <BreakdownBars title="Calls by reason" rows={reasonRows} />
          <BreakdownBars title="Calls by action taken" rows={outcomeRows} />
        </div>

        {/* Call list */}
        <Card className="gap-0 py-0 overflow-hidden">
          <div className="flex flex-col gap-3 p-4 border-b border-border">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold">Calls</h3>
                <span className="text-xs text-muted-foreground">
                  {filteredCalls.length} of {allCalls.length} total
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  aria-pressed={groupMode === "day"}
                  className={`h-7 text-xs ${toggleColors(groupMode === "day")}`}
                  onClick={() => setGroupMode("day")}
                >
                  <CalendarDays className="h-3 w-3 mr-1" />
                  By time
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-pressed={groupMode === "caller"}
                  className={`h-7 text-xs ${toggleColors(
                    groupMode === "caller"
                  )}`}
                  data-testid="group-by-caller"
                  onClick={() => setGroupMode("caller")}
                >
                  <Users className="h-3 w-3 mr-1" />
                  By caller
                </Button>
              </div>
            </div>
            {/* Search — spans caller, recap, next step, and transcript text */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  data-testid="call-search"
                  aria-label="Search calls"
                  placeholder="Search callers, recaps, and what was said on the call…"
                  className="h-8 pl-8 pr-8 text-xs"
                />
                {query !== "" && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    onClick={() => setQuery("")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Select
                value={sortMode}
                onValueChange={(value) => setSortMode(value as SortMode)}
              >
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SORT_MODE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Primary filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={practiceFilter} onValueChange={setPracticeFilter}>
                <SelectTrigger className="h-8 w-[190px] text-xs">
                  <SelectValue placeholder="Practice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All practices</SelectItem>
                  {practices.map((practice) => (
                    <SelectItem key={practice.id} value={practice.id}>
                      {practice.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={reasonFilter} onValueChange={setReasonFilter}>
                <SelectTrigger className="h-8 w-[170px] text-xs">
                  <SelectValue placeholder="Reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All reasons</SelectItem>
                  {Object.entries(REASON_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                <SelectTrigger className="h-8 w-[200px] text-xs">
                  <SelectValue placeholder="Outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All outcomes</SelectItem>
                  {Object.entries(OUTCOME_LABELS)
                    .filter(([value]) => value !== "APPOINTMENT_SCHEDULED")
                    .map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                className={`h-8 text-xs ${toggleColors(showAdvanced)}`}
                data-testid="toggle-advanced-filters"
                aria-expanded={showAdvanced}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <SlidersHorizontal className="h-3 w-3 mr-1" />
                More filters
                {advancedCount > 0 && ` (${advancedCount})`}
              </Button>
            </div>

            {/* Advanced filters */}
            {showAdvanced && (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 rounded-md border border-border bg-gray-50 p-3"
                data-testid="advanced-filters"
              >
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-10 shrink-0">From</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    max={dateTo === "" ? undefined : dateTo}
                    onChange={(event) => setDateFrom(event.target.value)}
                    data-testid="date-from"
                    className="h-8 text-xs"
                  />
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-10 shrink-0">To</span>
                  <Input
                    type="date"
                    value={dateTo}
                    min={dateFrom === "" ? undefined : dateFrom}
                    onChange={(event) => setDateTo(event.target.value)}
                    data-testid="date-to"
                    className="h-8 text-xs"
                  />
                </label>
                <Select value={callerFilter} onValueChange={setCallerFilter}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Caller" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All callers</SelectItem>
                    {callerOptions.map((caller) => (
                      <SelectItem
                        key={caller.normalizedPhone}
                        value={caller.normalizedPhone}
                      >
                        {caller.callerName} · {caller.displayPhone}
                        {caller.calls.length > 1
                          ? ` (${caller.calls.length})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={dayTypeFilter} onValueChange={setDayTypeFilter}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Day type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Any day</SelectItem>
                    {Object.entries(DAY_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={timeOfDayFilter}
                  onValueChange={setTimeOfDayFilter}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Time of day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Any time</SelectItem>
                    {Object.entries(TIME_OF_DAY_LABELS).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <Select
                  value={knowledgeFilter}
                  onValueChange={setKnowledgeFilter}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Knowledge source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Any knowledge source</SelectItem>
                    {Object.entries(KNOWLEDGE_SOURCE_LABELS).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 flex-wrap sm:col-span-2 lg:col-span-3">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-pressed={repeatCallersOnly}
                    className={`h-8 text-xs ${toggleColors(repeatCallersOnly)}`}
                    data-testid="repeat-callers-only"
                    onClick={() => setRepeatCallersOnly(!repeatCallersOnly)}
                  >
                    Repeat callers only
                  </Button>
                </div>
              </div>
            )}

            {/* Active filter summary */}
            {activeChips.length > 0 && (
              <div
                className="flex items-center gap-1.5 flex-wrap"
                data-testid="active-filters"
              >
                {activeChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 pl-2.5 pr-1 py-0.5 text-[11px] text-brand"
                  >
                    {chip.label}
                    <button
                      type="button"
                      aria-label={`Remove filter: ${chip.label}`}
                      className="rounded-full p-0.5 hover:bg-brand-100"
                      onClick={chip.clear}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  data-testid="reset-filters"
                  onClick={resetFilters}
                >
                  Reset all
                </Button>
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            {groupMode === "day" ? (
              <Table>
                {tableHeader}
                <TableBody>
                  {filteredCalls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No calls match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : showDaySeparators ? (
                    daySections.map((section) => (
                      <React.Fragment key={section.dayKey}>
                        {renderDaySeparatorRow(section)}
                        {section.calls.map(renderCallRow)}
                      </React.Fragment>
                    ))
                  ) : (
                    filteredCalls.map(renderCallRow)
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table>
                {tableHeader}
                <TableBody>
                  {callerGroups.length > 0 ? (
                    callerGroups.map((group) => (
                      <React.Fragment key={group.normalizedPhone}>
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableCell
                            colSpan={7}
                            className="py-2 text-xs font-medium text-gray-700"
                            data-testid={`caller-group-${group.normalizedPhone}`}
                          >
                            {group.callerName} · {group.displayPhone} ·{" "}
                            {group.calls.length}{" "}
                            {group.calls.length === 1 ? "call" : "calls"}
                          </TableCell>
                        </TableRow>
                        {group.calls.map(renderCallRow)}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No calls match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Mobile card view */}
          <div className="sm:hidden p-2 space-y-3">
            {filteredCalls.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">
                No calls match the current filters.
              </div>
            ) : showDaySeparators ? (
              daySections.map((section) => (
                <React.Fragment key={section.dayKey}>
                  <div
                    className="flex items-center gap-3 pt-1"
                    data-testid={`day-separator-mobile-${section.dayKey}`}
                  >
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {section.label}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  {section.calls.map(renderCallCard)}
                </React.Fragment>
              ))
            ) : (
              filteredCalls.map(renderCallCard)
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default InboundCallsPage;
